import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface FirmaAyari {
  id: string;
  firma_adi: string;
  yetkili_adi: string;
  telefon: string;
  whatsapp_numarasi: string;
  email: string;
  acik_adres: string;
  il: string;
  ilce: string;
  vergi_bilgisi: string;
  banka_adi: string;
  hesap_sahibi: string;
  iban: string;
  logo_url: string;
  varsayilan_bakim_suresi_ay: number;
  sari_baslangic_ay: number;
  turuncu_baslangic_ay: number;
  kirmizi_baslangic_ay: number;
  para_birimi: string;
}

interface FormState {
  firma_adi: string;
  yetkili_adi: string;
  telefon: string;
  whatsapp_numarasi: string;
  email: string;
  acik_adres: string;
  il: string;
  ilce: string;
  vergi_bilgisi: string;
  banka_adi: string;
  hesap_sahibi: string;
  iban: string;
  varsayilan_bakim_suresi_ay: string;
  sari_baslangic_ay: string;
  turuncu_baslangic_ay: string;
  kirmizi_baslangic_ay: string;
  para_birimi: string;
}

export default function FirmaAyarlarPage() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState<FirmaAyari | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState<FormState>({
    firma_adi: '', yetkili_adi: '', telefon: '', whatsapp_numarasi: '',
    email: '', acik_adres: '', il: '', ilce: '', vergi_bilgisi: '',
    banka_adi: '', hesap_sahibi: '', iban: '',
    varsayilan_bakim_suresi_ay: '6', sari_baslangic_ay: '6', turuncu_baslangic_ay: '9', kirmizi_baslangic_ay: '12',
    para_birimi: 'TL',
  });

  const load = useCallback(async () => {
    setLoading(true);
    const { data: row, error } = await supabase
      .from('sadem_firma_ayarlari')
      .select('*')
      .eq('aktif', true)
      .maybeSingle();
    if (!error && row) {
      setData(row as FirmaAyari);
      setForm({
        firma_adi: row.firma_adi || '',
        yetkili_adi: row.yetkili_adi || '',
        telefon: row.telefon || '',
        whatsapp_numarasi: row.whatsapp_numarasi || '',
        email: row.email || '',
        acik_adres: row.acik_adres || '',
        il: row.il || '',
        ilce: row.ilce || '',
        vergi_bilgisi: row.vergi_bilgisi || '',
        banka_adi: row.banka_adi || '',
        hesap_sahibi: row.hesap_sahibi || '',
        iban: row.iban || '',
        varsayilan_bakim_suresi_ay: String(row.varsayilan_bakim_suresi_ay || 6),
        sari_baslangic_ay: String(row.sari_baslangic_ay || 6),
        turuncu_baslangic_ay: String(row.turuncu_baslangic_ay || 9),
        kirmizi_baslangic_ay: String(row.kirmizi_baslangic_ay || 12),
        para_birimi: row.para_birimi || 'TL',
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) {
      setMessage({ type: 'error', text: 'Bu işlem için yönetici yetkisi gereklidir.' });
      return;
    }

    // Bakım eşik doğrulaması
    const sari = parseInt(form.sari_baslangic_ay, 10) || 6;
    const turuncu = parseInt(form.turuncu_baslangic_ay, 10) || 9;
    const kirmizi = parseInt(form.kirmizi_baslangic_ay, 10) || 12;

    if (sari < 1) {
      setMessage({ type: 'error', text: 'Sarı başlangıç ayı 1\'den küçük olamaz.' });
      return;
    }
    if (turuncu <= sari) {
      setMessage({ type: 'error', text: 'Bakım süreleri küçükten büyüğe doğru sıralanmalıdır. Turuncu başlangıç, sarı başlangıçtan büyük olmalıdır.' });
      return;
    }
    if (kirmizi <= turuncu) {
      setMessage({ type: 'error', text: 'Bakım süreleri küçükten büyüğe doğru sıralanmalıdır. Kırmızı başlangıç, turuncu başlangıçtan büyük olmalıdır.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    const payload = {
      firma_adi: form.firma_adi,
      yetkili_adi: form.yetkili_adi,
      telefon: form.telefon,
      whatsapp_numarasi: form.whatsapp_numarasi,
      email: form.email,
      acik_adres: form.acik_adres,
      il: form.il,
      ilce: form.ilce,
      vergi_bilgisi: form.vergi_bilgisi,
      banka_adi: form.banka_adi,
      hesap_sahibi: form.hesap_sahibi,
      iban: form.iban,
      varsayilan_bakim_suresi_ay: parseInt(form.varsayilan_bakim_suresi_ay, 10) || 6,
      sari_baslangic_ay: sari,
      turuncu_baslangic_ay: turuncu,
      kirmizi_baslangic_ay: kirmizi,
      para_birimi: form.para_birimi,
      updated_at: new Date().toISOString(),
    };

    const { error } = data?.id
      ? await supabase.from('sadem_firma_ayarlari').update(payload).eq('id', data.id)
      : await supabase.from('sadem_firma_ayarlari').insert(payload);

    if (error) {
      setMessage({ type: 'error', text: 'Bilgiler güncellenemedi. Lütfen tekrar deneyiniz.' });
    } else {
      setMessage({ type: 'success', text: 'Firma ayarları başarıyla güncellendi.' });
      await load();
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800">Firma Ayarları</h2>
        <p className="text-sm text-gray-500 mt-0.5">Firma bilgileri, banka bilgileri ve sistem ayarları</p>
      </div>

      {message && (
        <div className={`rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          <i className={`${message.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} text-lg`}></i>
          {message.text}
        </div>
      )}

      {!isAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-5 py-4 text-sm text-yellow-700 flex items-center gap-2">
          <i className="ri-lock-line text-lg"></i>
          <span>Bu sayfayı görüntülemek ve düzenlemek için yönetici yetkisi gereklidir.</span>
        </div>
      )}

      <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-100 p-5 md:p-6 space-y-6">
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
            <i className="ri-building-line text-sky-600"></i>
            Genel Bilgiler
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Firma Adı *</label>
              <input type="text" value={form.firma_adi} onChange={(e) => handleChange('firma_adi', e.target.value)} required disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Yetkili Adı</label>
              <input type="text" value={form.yetkili_adi} onChange={(e) => handleChange('yetkili_adi', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Telefon</label>
              <input type="text" value={form.telefon} onChange={(e) => handleChange('telefon', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp Numarası</label>
              <input type="text" value={form.whatsapp_numarasi} onChange={(e) => handleChange('whatsapp_numarasi', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">E-posta</label>
              <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Açık Adres</label>
              <textarea value={form.acik_adres} onChange={(e) => handleChange('acik_adres', e.target.value)} disabled={!isAdmin} rows={2}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50 resize-none" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">İl</label>
              <input type="text" value={form.il} onChange={(e) => handleChange('il', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">İlçe</label>
              <input type="text" value={form.ilce} onChange={(e) => handleChange('ilce', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Vergi Bilgisi</label>
              <input type="text" value={form.vergi_bilgisi} onChange={(e) => handleChange('vergi_bilgisi', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
            <i className="ri-bank-line text-sky-600"></i>
            Banka Bilgileri
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Banka Adı</label>
              <input type="text" value={form.banka_adi} onChange={(e) => handleChange('banka_adi', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Hesap Sahibi</label>
              <input type="text" value={form.hesap_sahibi} onChange={(e) => handleChange('hesap_sahibi', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
            <div className="md:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">IBAN</label>
              <input type="text" value={form.iban} onChange={(e) => handleChange('iban', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" /></div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
            <i className="ri-settings-3-line text-sky-600"></i>
            Bakım Kademe Ayarları
          </h3>
          <p className="text-xs text-gray-400 mb-4">Bakım durumlarının hangi aylarda değişeceğini belirleyin. Değişiklikler anında tüm müşterilere yansır.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-yellow-400 flex-shrink-0"></span>
                  Sarı Başlangıç (Ay)
                </span>
              </label>
              <input type="number" min={1} max={36} value={form.sari_baslangic_ay} onChange={(e) => handleChange('sari_baslangic_ay', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" />
              <p className="text-xs text-gray-400 mt-1">Varsayılan: 6 ay</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-orange-500 flex-shrink-0"></span>
                  Turuncu Başlangıç (Ay)
                </span>
              </label>
              <input type="number" min={1} max={36} value={form.turuncu_baslangic_ay} onChange={(e) => handleChange('turuncu_baslangic_ay', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" />
              <p className="text-xs text-gray-400 mt-1">Varsayılan: 9 ay</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></span>
                  Kırmızı Başlangıç (Ay)
                </span>
              </label>
              <input type="number" min={1} max={36} value={form.kirmizi_baslangic_ay} onChange={(e) => handleChange('kirmizi_baslangic_ay', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" />
              <p className="text-xs text-gray-400 mt-1">Varsayılan: 12 ay</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Varsayılan Bakım Süresi (Ay)</label>
              <input type="number" min={1} max={24} value={form.varsayilan_bakim_suresi_ay} onChange={(e) => handleChange('varsayilan_bakim_suresi_ay', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Para Birimi</label>
              <select value={form.para_birimi} onChange={(e) => handleChange('para_birimi', e.target.value)} disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50">
                <option value="TL">TL (Türk Lirası)</option>
                <option value="USD">USD (Dolar)</option>
                <option value="EUR">EUR (Euro)</option>
              </select>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="flex items-center justify-end pt-4 border-t border-gray-100">
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 cursor-pointer">
              {saving && <i className="ri-loader-4-line animate-spin"></i>}
              <span>{saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}</span>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}